import { useInfiniteQuery } from '@tanstack/react-query';
import ApplicantFilter from '@company/types/applicant-filter.type';
import {
  CandidateStage,
  NormalizedCandidateShape,
} from '@company/types/job-overview.type';
import * as api from '@company/services/application/api';
import {
  fromConsideredWorker,
  fromJobAssignment,
  fromJobPlacement,
  fromSuggestedResult,
} from '@company/helpers/job-overview-helper';
import { Pagination } from '@th-types/pageable.type';
import getNextPage from '@utils/Pagination';
import QueryKeys from '@constants/queryKeys';

const CANDIDATES_PAGE_SIZE = 10;

interface CandidatePage {
  results: NormalizedCandidateShape[];
  pagination: Pagination;
  restrictedAccess?: boolean;
}

type StageFetcher = (
  jobId: number,
  filters: ApplicantFilter,
  page: number,
  max: number
) => Promise<CandidatePage>;

const mapPage = <T>(
  data: { results: T[]; pagination: Pagination; restrictedAccess?: boolean },
  adapt: (item: T) => NormalizedCandidateShape
): CandidatePage => ({
  results: data.results.map(adapt),
  pagination: data.pagination,
  restrictedAccess: data.restrictedAccess,
});

const STAGE_FETCHERS: Partial<Record<CandidateStage, StageFetcher>> = {
  [CandidateStage.NEW]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchNewApplicants(jobId, f, page, max),
      fromJobAssignment
    ),
  [CandidateStage.APPLICANTS]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchApplicantAppliedList(jobId, f, page, max),
      fromJobAssignment
    ),
  [CandidateStage.CONSIDERING]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchConsideredWorkers(jobId, f, page, max),
      fromConsideredWorker
    ),
  [CandidateStage.SUGGESTED]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchSuggestedWorkers(jobId, f, page, max),
      fromSuggestedResult
    ),
  [CandidateStage.HIDDEN]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchApplicantHiddenList(jobId, f, page, max),
      fromJobAssignment
    ),
  [CandidateStage.OFFERED]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchApplicantOfferedList(jobId, f, page, max),
      fromJobPlacement
    ),
  [CandidateStage.HIRED]: async (jobId, f, page, max) =>
    mapPage(
      await api.fetchApplicantHiredList(jobId, f, page, max),
      fromJobAssignment
    ),
};

// TODO: when work on filters is resumed, this should be replaced with a real filter object.
const NO_FILTERS: ApplicantFilter = {
  nameOrEmail: '',
  database: undefined,
  gender: undefined,
  country: undefined,
  willingToTravel: undefined,
  travelLongerPeriod: undefined,
  sortBy: undefined,
  order: undefined,
};

export function useCandidatesQuery(
  jobId: number,
  stage: CandidateStage,
  filters: ApplicantFilter = NO_FILTERS
) {
  const fetcher = STAGE_FETCHERS[stage];
  const query = useInfiniteQuery({
    queryKey: [QueryKeys.JobDetail.CANDIDATES, jobId, stage, filters],
    queryFn: ({ pageParam }) =>
      fetcher!(jobId, filters, pageParam, CANDIDATES_PAGE_SIZE),
    initialPageParam: 0,
    // Restricted pages report the real total with an empty result list, so
    // never page past them.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.restrictedAccess ? undefined : getNextPage(lastPage, allPages),
    enabled: !!jobId && !!fetcher,
  });

  const candidates = query.data?.pages.flatMap((page) => page.results) ?? [];
  const total = query.data?.pages[0]?.pagination.count ?? 0;
  const remaining = Math.max(0, total - candidates.length);
  const restrictedAccess = query.data?.pages[0]?.restrictedAccess === true;

  return {
    candidates,
    remaining,
    restrictedAccess,
    isLoading: query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
